import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Plus, Search, LayoutGrid, List as ListIcon, X, Loader2, Car, Bike, Anchor,
  Armchair, Plane, Wrench, Package, ChevronRight, Clock,
} from "lucide-react";

export const SUPPORTS = {
  automobile: { label: "Automobile", icon: Car, color: "text-sky-600 bg-sky-50" },
  moto: { label: "Moto", icon: Bike, color: "text-rose-600 bg-rose-50" },
  nautique: { label: "Nautique", icon: Anchor, color: "text-cyan-600 bg-cyan-50" },
  mobilier: { label: "Mobilier", icon: Armchair, color: "text-amber-700 bg-amber-50" },
  aeronautique: { label: "Aéronautique", icon: Plane, color: "text-indigo-600 bg-indigo-50" },
  professionnel: { label: "Pro / Équipement", icon: Wrench, color: "text-slate-600 bg-slate-100" },
  autre: { label: "Autre", icon: Package, color: "text-slate-600 bg-slate-100" },
};
export const STATUSES = [
  { key: "nouveau", label: "Nouveau", dot: "bg-slate-400" },
  { key: "etude", label: "Étude & cotes", dot: "bg-sky-400" },
  { key: "devis", label: "Devis", dot: "bg-violet-400" },
  { key: "production", label: "Production", dot: "bg-amber-500" },
  { key: "controle", label: "Contrôle qualité", dot: "bg-indigo-400" },
  { key: "livraison", label: "Livraison", dot: "bg-blue-500" },
  { key: "termine", label: "Terminé", dot: "bg-emerald-500" },
];

const emptyForm = () => ({ title: "", client_name: "", support_type: "automobile", support_label: "", nature: "" });

function SupportBadge({ type }) {
  const s = SUPPORTS[type] || SUPPORTS.autre;
  const Icon = s.icon;
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${s.color}`}><Icon className="w-3 h-3" /> {s.label}</span>;
}

function ProjectCard({ p, onOpen }) {
  const doneCount = (p.stages || []).filter((s) => s.done).length;
  const total = (p.stages || []).length || 1;
  const pct = Math.round((doneCount / total) * 100);
  return (
    <button onClick={() => onOpen(p)} data-testid={`project-card-${p.id}`}
      className="w-full text-left bg-white rounded-xl border border-slate-200 p-3.5 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 group">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-mono text-slate-400">{p.reference}</span>
        <SupportBadge type={p.support_type} />
      </div>
      <div className="mt-2 font-display font-semibold text-navy text-sm leading-snug group-hover:text-sky-700">{p.title}</div>
      {p.support_label && <div className="text-xs text-slate-500 mt-0.5">{p.support_label}</div>}
      <div className="text-xs text-slate-400 mt-1">{p.client_name || "—"}</div>
      <div className="mt-3">
        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-sky-400 to-emerald-400 transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
        <div className="flex items-center justify-between mt-1.5 text-[11px] text-slate-400">
          <span>{doneCount}/{total} étapes</span>
          <span className="inline-flex items-center gap-1">{pct}% <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" /></span>
        </div>
      </div>
    </button>
  );
}

export default function Projects() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [view, setView] = useState("kanban");
  const [query, setQuery] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/projects").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const filtered = items.filter((p) => !query ||
    [p.title, p.reference, p.client_name, p.support_label].some((v) => (v || "").toLowerCase().includes(query.toLowerCase())));

  const open = (p) => navigate(`/projets/${p.id}`);

  const create = async () => {
    if (!form.title.trim()) return toast.error("Le titre du projet est obligatoire");
    setSaving(true);
    try {
      const { data } = await api.post("/projects", form);
      toast.success(`Projet ${data.reference} créé`);
      setShowForm(false); setForm(emptyForm());
      navigate(`/projets/${data.id}`);
    } catch { toast.error("Erreur lors de la création"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5 animate-fade-up" data-testid="projects-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl lg:text-3xl font-display font-bold text-navy tracking-tight">Projets & Ordres de fabrication</h1>
          <p className="text-sm text-slate-500 mt-0.5">Pilotez chaque ouvrage de la demande à la livraison.</p>
        </div>
        <button onClick={() => { setForm(emptyForm()); setShowForm(true); }} data-testid="project-add-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-navy text-white text-sm font-semibold hover:bg-navy-deep transition-all active:scale-95">
          <Plus className="w-4 h-4" /> Nouveau projet
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="project-search"
            placeholder="Rechercher un projet, un client, un support…"
            className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sky-400" />
        </div>
        <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1">
          <button onClick={() => setView("kanban")} data-testid="view-kanban" className={`px-3 py-1.5 rounded-lg text-sm font-medium inline-flex items-center gap-1.5 ${view === "kanban" ? "bg-navy text-white" : "text-slate-500"}`}><LayoutGrid className="w-4 h-4" /> Kanban</button>
          <button onClick={() => setView("list")} data-testid="view-list" className={`px-3 py-1.5 rounded-lg text-sm font-medium inline-flex items-center gap-1.5 ${view === "list" ? "bg-navy text-white" : "text-slate-500"}`}><ListIcon className="w-4 h-4" /> Liste</button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-sky-400/40 p-12 text-center" data-testid="projects-empty">
          <Clock className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Aucun projet pour l'instant. Créez votre premier ordre de fabrication.</p>
        </div>
      ) : view === "kanban" ? (
        <div className="flex gap-4 overflow-x-auto pb-3" data-testid="kanban-board">
          {STATUSES.map((col) => {
            const cards = filtered.filter((p) => p.status === col.key);
            return (
              <div key={col.key} className="w-72 shrink-0" data-testid={`kanban-col-${col.key}`}>
                <div className="flex items-center gap-2 px-1 mb-2.5">
                  <span className={`w-2 h-2 rounded-full ${col.dot}`} />
                  <span className="text-sm font-semibold text-navy">{col.label}</span>
                  <span className="text-xs text-slate-400">{cards.length}</span>
                </div>
                <div className="space-y-2.5 min-h-[60px]">
                  {cards.map((p) => <ProjectCard key={p.id} p={p} onOpen={open} />)}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
              <th className="text-left px-4 py-3 font-medium">Réf.</th><th className="text-left px-3 py-3 font-medium">Projet</th>
              <th className="text-left px-3 py-3 font-medium">Support</th><th className="text-left px-3 py-3 font-medium">Client</th>
              <th className="text-left px-3 py-3 font-medium">Statut</th><th className="text-right px-3 py-3 font-medium">Avancement</th>
            </tr></thead>
            <tbody>
              {filtered.map((p) => {
                const done = (p.stages || []).filter((s) => s.done).length, tot = (p.stages || []).length || 1;
                const st = STATUSES.find((s) => s.key === p.status);
                return (
                  <tr key={p.id} onClick={() => open(p)} data-testid={`project-row-${p.id}`} className="border-t border-slate-100 hover:bg-sky-50/40 cursor-pointer">
                    <td className="px-4 py-3 font-mono text-xs text-slate-400">{p.reference}</td>
                    <td className="px-3 py-3 font-semibold text-navy">{p.title}<div className="text-xs text-slate-400 font-normal">{p.support_label}</div></td>
                    <td className="px-3 py-3"><SupportBadge type={p.support_type} /></td>
                    <td className="px-3 py-3 text-slate-500">{p.client_name || "—"}</td>
                    <td className="px-3 py-3"><span className="inline-flex items-center gap-1.5 text-xs"><span className={`w-2 h-2 rounded-full ${st?.dot}`} />{st?.label}</span></td>
                    <td className="px-3 py-3 text-right font-mono text-xs text-slate-500">{done}/{tot}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setShowForm(false)} data-testid="project-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-display font-bold text-navy">Nouveau projet</h2>
              <button onClick={() => setShowForm(false)} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-500">Titre du projet *</label>
                <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="project-form-title"
                  placeholder="Réfection sièges avant cuir" className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-500">Type de support</label>
                  <select value={form.support_type} onChange={(e) => setForm({ ...form, support_type: e.target.value })} data-testid="project-form-support"
                    className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sky-400">
                    {Object.entries(SUPPORTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">Support (marque/modèle)</label>
                  <input value={form.support_label} onChange={(e) => setForm({ ...form, support_label: e.target.value })} data-testid="project-form-support-label"
                    placeholder="Peugeot 205 GTI" className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Client</label>
                <input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="project-form-client"
                  className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Nature du besoin</label>
                <textarea value={form.nature} onChange={(e) => setForm({ ...form, nature: e.target.value })} rows={2} data-testid="project-form-nature"
                  className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium hover:bg-slate-50">Annuler</button>
              <button onClick={create} disabled={saving} data-testid="project-form-create" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-navy text-white text-sm font-semibold hover:bg-navy-deep disabled:opacity-60">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Créer le projet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
